import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  FolderOpen,
  CheckSquare,
  DollarSign,
  TrendingUp,
  Clock,
  Users,
  Calendar,
  AlertCircle,
  Target,
  Award,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

const pieColors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

interface Task {
  _id: string;
  title: string;
  status: string;
  priority: string;
  dueDate: string;
  project: {
    _id: string;
    title: string;
  };
}

interface Project {
  _id: string;
  title: string;
  status: string;
  progress: number;
  deadline: string;
}

interface DashboardStats {
  totalProjects: number;
  activeTasks: number;
  completedTasks: number;
  totalEarnings: number;
  pendingEarnings: number;
  availableBalance: number;
}

const DeveloperDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    totalProjects: 0,
    activeTasks: 0,
    completedTasks: 0,
    totalEarnings: 0,
    pendingEarnings: 0,
    availableBalance: 0,
  });
  const [chartData, setChartData] = useState<{ bar: any[]; pie: any[] }>({
    bar: [],
    pie: [],
  });

  const [loading, setLoading] = useState(true);

  const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // Special handling for WebContainer preview URLs
    if (hostname.includes('webcontainer.io') || hostname.includes('webcontainer-api.io')) {
      const baseUrl = hostname.replace('--5173--', '--5002--');
      return `${protocol}//${baseUrl}/api`;
    }

    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5002/api`;
    }

    // Render or other deployment domains
    return `${protocol}//${hostname}/api`;
  }

  // Fallback to env or default
  return import.meta.env.VITE_API_URL || 'http://localhost:5002/api';
};

const API_BASE_URL = getApiBaseUrl();

  // Redirect based on user role
  useEffect(() => {
    if (user && user.role !== 'developer') {
      if (user.role === 'client') {
        navigate('/dashboard/client', { replace: true });
      } else if (user.role === 'admin') {
        navigate('/dashboard/admin', { replace: true });
      }
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user && user.role === 'developer') {
      fetchDashboardData();
    }
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('accessToken');

      // Fetch all data in parallel
      const [tasksRes, projectsRes, earningsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/tasks?assignedTo=${user?._id}&limit=5`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/projects?developer=${user?._id}&limit=5`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/payments?developer=${user?._id}&summary=true`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const [tasksData, projectsData, earningsData] = await Promise.all([
        tasksRes.json(),
        projectsRes.json(),
        earningsRes.json()
      ]);

      // Set tasks and projects
      setTasks(tasksData.tasks || []);
      setProjects(projectsData.projects || []);

      // Calculate stats
      const activeTasks = tasksData.tasks?.filter((t: Task) => t.status !== 'completed').length || 0;
      const completedTasks = tasksData.tasks?.filter((t: Task) => t.status === 'completed').length || 0;
      const totalProjects = projectsData.projects?.length || 0;

      // Calculate earnings from payments data
      const totalEarnings = earningsData.payments?.reduce((sum: number, p: any) => 
        sum + (p.status === 'completed' ? p.amount : 0), 0) || 0;
      const pendingEarnings = earningsData.payments?.reduce((sum: number, p: any) => 
        sum + (p.status === 'pending' ? p.amount : 0), 0) || 0;
      const availableBalance = earningsData.withdrawals?.available || 0;

      setStats({
        totalProjects,
        activeTasks,
        completedTasks,
        totalEarnings,
        pendingEarnings,
        availableBalance
      });

      // Prepare chart data
      setChartData({
        bar: [
          { name: 'Total Tasks', value: tasksData.tasks?.length || 0 },
          { name: 'Active', value: activeTasks },
          { name: 'Completed', value: completedTasks }
        ],
        pie: [
          { name: 'Active', value: activeTasks },
          { name: 'Completed', value: completedTasks },
          { 
            name: 'Pending', 
            value: (tasksData.tasks?.length || 0) - activeTasks - completedTasks 
          }
        ]
      });

    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'in-progress':
        return 'text-blue-600 bg-blue-100';
      case 'todo':
        return 'text-gray-600 bg-gray-100';
      case 'review':
        return 'text-purple-600 bg-purple-100';
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'cancelled':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'text-red-600 bg-red-100';
      case 'high':
        return 'text-orange-600 bg-orange-100';
      case 'medium':
        return 'text-yellow-600 bg-yellow-100';
      case 'low':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const getTimeUntilDue = (dueDate: string) => {
    const days = Math.ceil(
      (new Date(dueDate).getTime() - new Date().getTime()) /
        (1000 * 60 * 60 * 24)
    );
    if (days < 0) return `${Math.abs(days)} days overdue`;
    if (days === 0) return 'Due today';
    if (days === 1) return 'Due tomorrow';
    return `Due in ${days} days`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-16 bg-gray-200 rounded-lg animate-pulse"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, index) => (
            <div
              key={index}
              className="bg-white p-6 rounded-lg shadow-sm border animate-pulse"
            >
              <div className="h-4 bg-gray-200 rounded w-20 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-16 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-24"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Good morning, {user?.firstName}!
          </h1>
          <p className="text-gray-600 mt-1">
            You have {stats.activeTasks} active tasks across{' '}
            {stats.totalProjects} projects.
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Link
            to="/dashboard/earnings"
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
          >
            <DollarSign className="w-4 h-4 mr-2" />
            View Earnings
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Active Projects
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.totalProjects}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <FolderOpen className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600">
              {projects.filter(p => p.status === 'active').length} active
            </span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Tasks</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.activeTasks}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <CheckSquare className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <Target className="w-4 h-4 text-blue-500 mr-1" />
            <span className="text-gray-600">
              {stats.completedTasks} completed
            </span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Available Balance
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(stats.availableBalance)}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className="text-gray-600">
              {formatCurrency(stats.pendingEarnings)} pending
            </span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">
                Total Earnings
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(stats.totalEarnings)}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Award className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600">
              {stats.totalEarnings > 0 ? '+15% this month' : 'No earnings yet'}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold mb-4 text-gray-900">
            Task Overview
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData.bar}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold mb-4 text-gray-900">
            Task Distribution
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={chartData.pie}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label
              >
                {chartData.pie.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={pieColors[index % pieColors.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Tasks */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Recent Tasks
              </h2>
              <Link
                to="/dashboard/tasks"
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                View all
              </Link>
            </div>
          </div>

          <div className="p-6">
            {tasks.length === 0 ? (
              <div className="text-center py-8">
                <CheckSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-sm font-medium text-gray-900 mb-2">
                  No tasks assigned
                </h3>
                <p className="text-sm text-gray-600">
                  Tasks will appear here when assigned to you.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {tasks.map((task) => (
                  <div
                    key={task._id}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <Link
                          to={`/dashboard/tasks/${task._id}`}
                          className="font-medium text-gray-900 hover:text-blue-600 truncate"
                        >
                          {task.title}
                        </Link>
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getStatusColor(
                            task.status
                          )}`}
                        >
                          {task.status.replace('-', ' ')}
                        </span>
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getPriorityColor(
                            task.priority
                          )}`}
                        >
                          {task.priority}
                        </span>
                      </div>

                      <div className="mt-1 flex items-center space-x-4 text-sm text-gray-600">
                        <span>{task.project?.title}</span>
                        {task.dueDate && (
                          <div className="flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            {getTimeUntilDue(task.dueDate)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Current Projects */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                Current Projects
              </h2>
              <Link
                to="/dashboard/projects"
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                View all
              </Link>
            </div>
          </div>

          <div className="p-6">
            {projects.length === 0 ? (
              <div className="text-center py-8">
                <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-sm font-medium text-gray-900 mb-2">
                  No active projects
                </h3>
                <p className="text-sm text-gray-600">
                  Projects will appear here when you're assigned.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {projects.map((project) => (
                  <div
                    key={project._id}
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    <div className="flex items-center justify-between">
                      <Link
                        to={`/dashboard/projects/${project._id}`}
                        className="font-medium text-gray-900 hover:text-blue-600"
                      >
                        {project.title}
                      </Link>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${getStatusColor(
                          project.status
                        )}`}
                      >
                        {project.status}
                      </span>
                    </div>

                    <div className="mt-3">
                      <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                        <span>Progress</span>
                        <span>{project.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${project.progress}%` }}
                        />
                      </div>
                    </div>

                    <div className="mt-3 flex items-center text-sm text-gray-600">
                      <Calendar className="w-4 h-4 mr-1" />
                      Due {new Date(project.deadline).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link
          to="/dashboard/tasks"
          className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 rounded-lg text-white hover:from-blue-600 hover:to-blue-700 transition-all duration-200 transform hover:scale-105"
        >
          <CheckSquare className="w-8 h-8 mb-4" />
          <h3 className="text-lg font-semibold mb-2">View Tasks</h3>
          <p className="text-blue-100">Manage and update your assigned tasks</p>
        </Link>

        <Link
          to="/dashboard/earnings"
          className="bg-gradient-to-r from-green-500 to-green-600 p-6 rounded-lg text-white hover:from-green-600 hover:to-green-700 transition-all duration-200 transform hover:scale-105"
        >
          <DollarSign className="w-8 h-8 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Earnings</h3>
          <p className="text-green-100">
            Track earnings and request withdrawals
          </p>
        </Link>

        <Link
          to="/dashboard/chat"
          className="bg-gradient-to-r from-purple-500 to-purple-600 p-6 rounded-lg text-white hover:from-purple-600 hover:to-purple-700 transition-all duration-200 transform hover:scale-105"
        >
          <Users className="w-8 h-8 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Team Chat</h3>
          <p className="text-purple-100">Communicate with clients and team</p>
        </Link>
      </div>
    </div>
  );
};

export default DeveloperDashboard;